import { useNavigate } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowUpRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/auth.store"

export function FinalCTA({ className }: { className?: string } = {}) {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  return (
    <section
      className={cn(
        "w-full bg-gradient-to-b from-white via-[#fef3c7]/15 to-[#fef3c7]/25 dark:from-slate-900 dark:via-slate-900/60 dark:to-slate-950 pb-20 md:pb-24",
        className
      )}
    >
      <div className="max-w-7xl xl:max-w-[clamp(1280px,92vw,1440px)] 2xl:max-w-[clamp(1280px,92vw,1560px)] mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-[#6cb8fd] via-[#aedbfd] to-[#d8ecfc] shadow-xl border border-blue-200/60 p-6 sm:p-8 lg:py-8 lg:px-10 flex flex-col items-center text-center"
        >
          {/* Atmospheric Cloud Glows at the center and bottom */}
          {/* Center cloud drifts */}
          <div className="absolute top-[38%] -left-10 w-[480px] h-48 bg-white/45 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-[48%] left-[28%] w-[520px] h-52 bg-white/50 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-[42%] -right-6 w-[460px] h-48 bg-white/40 rounded-full blur-3xl pointer-events-none" />

          {/* Bottom cloud puffs */}
          <div className="absolute -bottom-10 left-8 w-[420px] h-44 bg-white/55 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[600px] h-44 bg-white/60 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 right-6 w-[440px] h-44 bg-white/50 rounded-full blur-2xl pointer-events-none" />

          {/* Diffuse atmospheric cloud lighting */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_75%,rgba(255,255,255,0.4),transparent_65%)] pointer-events-none" />

          {/* Content Wrapper */}
          <div className="flex flex-col items-center justify-center gap-3 sm:gap-3.5 relative z-10 text-slate-900 max-w-3xl mx-auto">
            <h2 className="font-sans text-xl sm:text-2xl md:text-[26px] lg:text-[28px] font-black leading-tight tracking-tight text-slate-900 select-none">
              <span className="whitespace-nowrap">Ready to transform</span>{" "}
              <span className="whitespace-nowrap">your B2B supply chain?</span>
            </h2>

            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed max-w-2xl font-normal">
              Join enterprise procurement teams and audited Indian manufacturers using ENGA to automate RFQs, eliminate middlemen, and protect capital with milestone escrow.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col min-[480px]:flex-row items-center justify-center gap-2.5 sm:gap-3 mt-1 sm:mt-1.5 w-full">
              <Button
                type="button"
                onClick={() => navigate({ to: isAuthenticated ? "/submit-rfq" : "/signup" })}
                className="relative text-[13px] sm:text-sm font-bold rounded-full h-11 p-1 ps-5.5 pe-12 group transition-all duration-500 hover:ps-12 hover:pe-5.5 w-auto overflow-hidden cursor-pointer bg-primary text-primary-foreground hover:bg-primary-hover shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
              >
                <span className="relative z-10 transition-all duration-500 whitespace-nowrap">
                  Request Quote
                </span>
                <div className="absolute right-1 w-9 h-9 bg-white text-slate-900 rounded-full flex items-center justify-center transition-all duration-500 group-hover:right-[calc(100%-40px)] group-hover:rotate-45 shadow-xs">
                  <ArrowUpRight size={15} />
                </div>
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/explore" })}
                className="w-auto rounded-full bg-white hover:bg-slate-50 border border-slate-200/90 text-slate-800 px-5.5 sm:px-6 h-11 text-[13px] sm:text-sm font-semibold shadow-sm transition-all active:scale-95 cursor-pointer justify-center whitespace-nowrap"
              >
                Start Explore
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
